import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

import type { BeforeInstallPromptEvent, ManualInstallPlatform } from './InstallPromptContext.helpers'
import { getIsStandalone, getManualInstallPlatform } from './InstallPromptContext.helpers'

export type InstallPromptContextType = {
  canPromptInstall: boolean
  promptInstall: () => Promise<'accepted' | 'dismissed' | null>
  manualInstallPlatform: ManualInstallPlatform | null
  isStandalone: boolean
}

const InstallPromptContext = createContext<InstallPromptContextType>({
  canPromptInstall: false,
  promptInstall: async () => null,
  manualInstallPlatform: null,
  isStandalone: false,
})

// This provider must be mounted at the app root: `beforeinstallprompt` fires once, shortly after
// page load, and is never replayed. Mounting here means the listener is attached on the first
// render — before the event fires — and the deferred event is held in state so consumers that
// mount much later (the post-login SideNav install item) can still read it.
const InstallPromptProvider = ({ children }: { children: React.ReactNode }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isStandalone, setIsStandalone] = useState(getIsStandalone)

  useEffect(() => {
    // Typed as plain Event because `beforeinstallprompt` is not in lib.dom's WindowEventMap
    const handleBeforeInstallPrompt = (event: Event) => {
      // Stop the browser showing its own mini-infobar so our button is the only entry point.
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }

    const handleAppInstalled = () => {
      setDeferredPrompt(null)
      setIsStandalone(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null

    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    // The prompt can only be used once, so drop it regardless of the outcome.
    setDeferredPrompt(null)

    return outcome
  }, [deferredPrompt])

  const value = {
    canPromptInstall: !!deferredPrompt && !isStandalone,
    promptInstall,
    manualInstallPlatform: getManualInstallPlatform(isStandalone),
    isStandalone,
  }

  return <InstallPromptContext.Provider value={value}>{children}</InstallPromptContext.Provider>
}

const useInstallPrompt = () => useContext(InstallPromptContext)

export { InstallPromptContext, InstallPromptProvider, useInstallPrompt }
